import { decimalSeparator, formatLocale } from "./locale";

const formatters = new Map<string, Intl.NumberFormat>();

const formatter = (options?: Intl.NumberFormatOptions) => {
  const locale = formatLocale();
  const key = `${locale}|${JSON.stringify(options ?? {})}`;
  let cached = formatters.get(key);
  if (!cached) {
    cached = new Intl.NumberFormat(locale, options);
    formatters.set(key, cached);
  }
  return cached;
};

const withDecimalSeparator = (text: string) => {
  const decimal = decimalSeparator();
  return decimal === "." ? text : text.replace(".", decimal);
};

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
export function formatNumber(value: number | null | undefined, options?: Intl.NumberFormatOptions): string | undefined;
export function formatNumber(value: number | null | undefined, options?: Intl.NumberFormatOptions) {
  return value == null ? undefined : formatter(options).format(value);
}

// Note: toFixed rounds the binary value (1.005 gives "1.00") where Intl rounds the decimal one ("1.01"), and it
// never groups, so this keeps toFixed's digits and changes only the separator.
export function formatFixed(value: number, digits: number): string;
export function formatFixed(value: number | null | undefined, digits: number): string | undefined;
export function formatFixed(value: number | null | undefined, digits: number) {
  return value == null ? undefined : withDecimalSeparator(value.toFixed(digits));
}

export function formatPlain(value: number): string;
export function formatPlain(value: number | null | undefined): string | undefined;
export function formatPlain(value: number | null | undefined) {
  return value == null ? undefined : withDecimalSeparator(String(value));
}

export const formatRounded = (value: number, digits: number) => formatPlain(Number(value.toFixed(digits)));

export const formatMetres = (value: number) => formatRounded(value, 2);

const percentAffixes = new Map<string, [string, string]>();

const percentAffix = (): [string, string] => {
  const locale = formatLocale();
  let affix = percentAffixes.get(locale);
  if (!affix) {
    const parts = new Intl.NumberFormat(locale, { style: "percent" }).formatToParts(1);
    const number = parts.findIndex(part => part.type === "integer");
    affix = [parts.slice(0, number), parts.slice(number + 1)].map(side => side.map(part => part.value).join("")) as [string, string];
    percentAffixes.set(locale, affix);
  }
  return affix;
};

export const withPercentSign = (number: string) => {
  const [before, after] = percentAffix();
  return `${before}${number}${after}`;
};

export function formatPercent(percentage: number, digits?: number): string;
export function formatPercent(percentage: number | null | undefined, digits?: number): string | undefined;
export function formatPercent(percentage: number | null | undefined, digits?: number) {
  return percentage == null ? undefined : withPercentSign(digits == null ? formatPlain(percentage) : formatFixed(percentage, digits));
}
